import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faImage,
  faPaperPlane,
  faTrashAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import useAI from "../context/AIContext";
import ChatMessageContent from "./ChatMessageContent";
import {
  getUserFile,
} from "../context/FuncionesContext";

export default function ChatAI() {
  const [draft, setDraft] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const [isPickingImage, setIsPickingImage] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const messagesEndRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const isChatOpen = useAI((state) => state.isChatOpen);
  const currentChat = useAI((state) => state.currentChat);
  const closeChat = useAI((state) => state.closeChat);
  const clearChat = useAI((state) => state.clearChat);
  const sendMessage = useAI((state) => state.sendMessage);
  const chats = useAI((state) => state.chats);
  const loadingByChat = useAI((state) => state.loadingByChat);
  const errorByChat = useAI((state) => state.errorByChat);
  const currentAiConf = useAI((state) => state.currentAiConf);

  const currentMessages = useMemo(
    () => chats[currentChat] || [],
    [chats, currentChat],
  );
  const isLoading = currentChat ? Boolean(loadingByChat[currentChat]) : false;
  const error = currentChat ? errorByChat[currentChat] : null;
  const aceptaImagenes = currentAiConf.acepta_imagenes === "s";
  const aceptaCamara = currentAiConf.acepta_camara === "s";

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentMessages, isLoading, isChatOpen, pendingImage]);

  useEffect(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setPendingImage(null);
    setAttachmentError("");
    setIsPickingImage(false);
    setCameraOpen(false);
    setCameraError("");
  }, [currentChat]);

  useEffect(() => {
    if (cameraOpen && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraOpen]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  const setPendingImageFromFile = (file) => {
    if (!file?.type?.startsWith("image/")) {
      setAttachmentError("Solo podes adjuntar archivos de imagen.");
      return false;
    }

    if (pendingImage?.previewUrl) {
      URL.revokeObjectURL(pendingImage.previewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setPendingImage({
      name: file.name,
      type: file.type,
      file,
      previewUrl,
      url: previewUrl,
    });
    setAttachmentError("");
    return true;
  };

  const handleAttachImage = async () => {
    if (!currentChat || isLoading || isPickingImage) return;

    try {
      setAttachmentError("");
      setIsPickingImage(true);
      const file = await getUserFile();
      setPendingImageFromFile(file);
    } catch (uploadError) {
      if (uploadError?.message !== "No file selected") {
        setAttachmentError("No se pudo adjuntar la imagen.");
      }
    } finally {
      setIsPickingImage(false);
    }
  };

  const closeCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
    setCameraError("");
  };

  const handleOpenCamera = async () => {
    if (!currentChat || isLoading || isPickingImage) return;

    setAttachmentError("");
    setCameraError("");

    if (!navigator.mediaDevices?.getUserMedia) {
      if (cameraInputRef.current) cameraInputRef.current.click();
      return;
    }

    setIsPickingImage(true);
    try {
      cameraStreamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      setCameraOpen(true);
    } catch (cameraAccessError) {
      setAttachmentError("No se pudo acceder a la camara.");
    } finally {
      setIsPickingImage(false);
    }
  };

  const handleCameraFileChange = (event) => {
    const file = event.currentTarget.files?.[0];
    if (file) setPendingImageFromFile(file);
    event.currentTarget.value = "";
  };

  const handleCapturePhoto = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) {
      setCameraError("La camara todavia no esta lista.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("No se pudo capturar la imagen.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    if (!blob) {
      setCameraError("No se pudo capturar la imagen.");
      return;
    }

    const file = new File([blob], `camara-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    closeCamera();
    setPendingImageFromFile(file);
  };

  const handleRemoveImage = () => {
    if (pendingImage?.previewUrl) {
      URL.revokeObjectURL(pendingImage.previewUrl);
    }
    setPendingImage(null);
    setAttachmentError("");
  };

  const handleClearChat = () => {
    if (!currentChat) return;
    handleRemoveImage();
    setDraft("");
    clearChat(currentChat);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!currentChat || (!draft.trim() && !pendingImage) || isLoading || isPickingImage) {
      return;
    }

    const nextDraft = draft;
    const nextImage = pendingImage;
    setDraft("");
    setPendingImage(null);
    setAttachmentError("");

    try {
      await sendMessage(currentChat, nextDraft, nextImage);
    } catch (err) {
      setDraft(nextDraft);
      setPendingImage(nextImage);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const getAttachmentUrl = (attachment) => {
    if (!attachment) return null;
    if (typeof attachment === "string") return attachment;
    return attachment.url || attachment.previewUrl || attachment.src || null;
  };

  return (
    <aside
      id="ai-chat"
      className={isChatOpen ? "ai-chat-open" : "ai-chat-closed"}
      data-component="ai-chat"
      aria-hidden={!isChatOpen}
    >
      <div className="ai-chat_header">
        <div className="ai-chat_titleWrap">
          <p className="ai-chat_eyebrow">Asistente IA</p>
          <h3 className="ai-chat_title">{currentAiConf.label || "Chat"}</h3>
        </div>
        <div className="ai-chat_headerActions">
          <button
            type="button"
            className="ai-chat_headerButton"
            onClick={handleClearChat}
            aria-label="Limpiar chat"
            title="Limpiar chat"
            disabled={!currentChat}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
          <button
            type="button"
            className="ai-chat_headerButton ai-chat_close"
            onClick={closeChat}
            aria-label="Cerrar chat"
            title="Cerrar chat"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <div className="ai-chat_messages">
        {currentMessages.length === 0 ? (
          <div className="ai-chat_empty">
            <p>Haceme una pregunta sobre esta pantalla.</p>
          </div>
        ) : (
          currentMessages.map((message, index) => (
            <div
              key={`${message.createdAt || "msg"}-${index}`}
              className={`ai-chat_message ai-chat_message-${message.role}`}
            >
              <span className="ai-chat_messageRole">
                {message.role === "user" ? "Vos" : "IA"}
              </span>
              {getAttachmentUrl(message.attachment) ? (
                <div className="ai-chat_attachmentBubble">
                  <img
                    src={getAttachmentUrl(message.attachment)}
                    alt={message.attachment.name || "Adjunto"}
                    className="ai-chat_attachmentImage"
                  />
                  <span className="ai-chat_attachmentName">
                    {message.attachment.name || "Imagen adjunta"}
                  </span>
                </div>
              ) : null}
              {message.content ? (
                <ChatMessageContent content={message.content} />
              ) : null}
            </div>
          ))
        )}

        {isLoading ? (
          <div className="ai-chat_message ai-chat_message-ai ai-chat_message-loading">
            <span className="ai-chat_messageRole">IA</span><br/>
            <p className="ai-chat_messageContent ai-chat_typing" aria-live="polite">
              <span>Escribiendo</span>
              <span className="ai-chat_typingDots" aria-hidden="true">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </p>
          </div>
        ) : null}

        {error ? <p className="ai-chat_error">{error}</p> : null}
        {attachmentError ? <p className="ai-chat_error">{attachmentError}</p> : null}
        <div ref={messagesEndRef} />
      </div>

      <form className="ai-chat_form" onSubmit={handleSubmit}>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="ai-chat_cameraInput"
          onChange={handleCameraFileChange}
        />

        {pendingImage ? (
          <div className="ai-chat_pendingAttachment">
            <img
              src={pendingImage.url}
              alt={pendingImage.name || "Imagen adjunta"}
              className="ai-chat_pendingAttachmentImage"
            />
            <div className="ai-chat_pendingAttachmentMeta">
              <span className="ai-chat_pendingAttachmentLabel">Imagen adjunta</span>
              <span className="ai-chat_pendingAttachmentName">
                {pendingImage.name || "archivo"}
              </span>
            </div>
            <button
              type="button"
              className="ai-chat_pendingAttachmentRemove"
              onClick={handleRemoveImage}
              aria-label="Quitar imagen adjunta"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ) : null}

        <div className="ai-chat_formRow">
          {aceptaCamara || aceptaImagenes ? (
            <div className="ai-chat_attachmentActions">
              {aceptaCamara ? (
                <button
                  type="button"
                  className="ai-chat_attach"
                  onClick={handleOpenCamera}
                  disabled={!currentChat || isLoading || isPickingImage}
                  aria-label="Usar camara"
                  title="Usar camara"
                >
                  <FontAwesomeIcon icon={faCamera} />
                </button>
              ) : null}

              {aceptaImagenes ? (
                <button
                  type="button"
                  className="ai-chat_attach"
                  onClick={handleAttachImage}
                  disabled={!currentChat || isLoading || isPickingImage}
                  aria-label="Adjuntar imagen"
                  title="Adjuntar imagen"
                >
                  <FontAwesomeIcon icon={faImage} />
                </button>
              ) : null}
            </div>
          ) : null}

          <textarea
            className="ai-chat_input"
            placeholder={
              isPickingImage ? "Preparando imagen..." : "Escribi tu mensaje..."
            }
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            disabled={!currentChat || isLoading || isPickingImage}
          />

          <button
            type="submit"
            className="ai-chat_send"
            disabled={
              !currentChat ||
              (!draft.trim() && !pendingImage) ||
              isLoading ||
              isPickingImage
            }
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>

        {cameraOpen ? (
          <div className="ai-chat_cameraBackdrop" role="dialog" aria-modal="true">
            <div className="ai-chat_cameraDialog">
              <video
                ref={videoRef}
                className="ai-chat_cameraVideo"
                autoPlay
                playsInline
                muted
              />
              {cameraError ? (
                <p className="ai-chat_error">{cameraError}</p>
              ) : null}
              <div className="ai-chat_cameraActions">
                <button
                  type="button"
                  className="ai-chat_cameraButton ai-chat_cameraButton-primary"
                  onClick={handleCapturePhoto}
                >
                  Tomar foto
                </button>
                <button
                  type="button"
                  className="ai-chat_cameraButton"
                  onClick={closeCamera}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </aside>
  );
}
